import './styles/fonts.css';
import './style.css';
import './redesign.css';
import logger, { initGlobalErrorHandlers } from './utils/logger.js';
import { createAdminComponent } from './components/Admin.js';
import { apiFetch } from './utils/apiClient.js';
import { showSuccessModal } from './components/ui/SuccessModal.js';

// Initialize global error handlers immediately
initGlobalErrorHandlers();
const log = logger.withContext('Main');

let authToken = localStorage.getItem('launcher_auth_token') || null;
let currentUser = null;

// Fonction globale pour afficher des notifications
function showGlobalNotification(type, message, duration = 3000) {
    // Pour les messages de succès, utiliser la belle modal
    if (type === 'success' && (message.includes('envoyé avec succès') || message.includes('succès') || message.includes('enregistré'))) {
        showSuccessModal(message, 'Succès');
        return;
    }

    // Pour les autres types, utiliser la notification classique
    let notificationModal = document.getElementById('global-notification-modal');
    if (!notificationModal) {
        notificationModal = document.createElement('div');
        notificationModal.id = 'global-notification-modal';
        notificationModal.className = 'modal modal-notification';
        notificationModal.style.display = 'none';
        notificationModal.innerHTML = `
            <div class="modal-content modal-notification-content">
                <div class="modal-notification-icon" id="global-notification-icon"></div>
                <div class="modal-notification-message" id="global-notification-message"></div>
                <button class="modal-close" id="global-notification-close" aria-label="Fermer">&times;</button>
            </div>
        `;
        document.body.appendChild(notificationModal);

        // Event listeners
        const closeBtn = notificationModal.querySelector('#global-notification-close');
        if (closeBtn) {
            closeBtn.addEventListener('click', () => closeGlobalNotification());
        }
        notificationModal.addEventListener('click', (e) => {
            if (e.target === notificationModal) {
                closeGlobalNotification();
            }
        });
    }

    const notificationIcon = notificationModal.querySelector('#global-notification-icon');
    const notificationMessage = notificationModal.querySelector('#global-notification-message');

    if (!notificationIcon || !notificationMessage) return;

    // Définir l'icône et le style selon le type
    let icon = '';
    let iconClass = '';
    if (type === 'success') {
        icon = 'check_circle';
        iconClass = 'notification-success';
    } else if (type === 'error') {
        icon = 'error';
        iconClass = 'notification-error';
    } else if (type === 'warning') {
        icon = 'warning';
        iconClass = 'notification-warning';
    } else {
        icon = 'info';
        iconClass = 'notification-info';
    }

    notificationIcon.innerHTML = `<span class="material-symbols-outlined" aria-hidden="true">${icon}</span>`;
    notificationIcon.className = `modal-notification-icon ${iconClass}`;
    notificationMessage.textContent = message;
    notificationModal.className = `modal modal-notification ${iconClass}`;
    notificationModal.style.display = 'flex';

    // Fermer automatiquement après la durée spécifiée
    if (duration > 0) {
        setTimeout(() => {
            closeGlobalNotification();
        }, duration);
    }
}

function closeGlobalNotification() {
    const notificationModal = document.getElementById('global-notification-modal');
    if (notificationModal) {
        notificationModal.style.display = 'none';
    }
}

function getAuthToken() {
    return authToken;
}

function setAuthToken(token) {
    authToken = token ? token.trim() : null;
    if (authToken) {
        localStorage.setItem('launcher_auth_token', authToken);
    } else {
        localStorage.removeItem('launcher_auth_token');
    }
    return authToken;
}

function setCurrentUser(user) {
    log.info('Setting current user', { userId: user?._id, email: user?.email });
    currentUser = user || null;
    if (!currentUser) {
        showAuthOverlay();
    }
    return currentUser;
}

function buildRequestOptions(options = {}) {
    const config = { ...options };
    const headers = new Headers(config.headers || {});
    const token = getAuthToken();

    if (token) {
        headers.set('Authorization', `Bearer ${token}`);
    }

    if (config.body && !(config.body instanceof FormData) && !headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json');
    }

    config.headers = headers;
    return config;
}

async function authFetch(path, options = {}) {
    const { skipAuthHandling = false, ...rest } = options || {};
    log.debug('authFetch', { path, method: options.method || 'GET' });

    try {
        const response = await apiFetch(path, buildRequestOptions(rest));

        if (response.status === 401 && !skipAuthHandling) {
            log.warn('Received 401, handling unauthorized', { path });
            await handleUnauthorized();
        }

        return response;
    } catch (error) {
        log.error('authFetch failed', { path, error });
        throw error;
    }
}

async function handleUnauthorized() {
    setAuthToken(null);
    setCurrentUser(null);
    showAuthOverlay();
}

async function fetchCurrentUser() {
    if (!getAuthToken()) {
        log.debug('No auth token, skipping fetchCurrentUser');
        return null;
    }

    try {
        log.debug('Fetching current user...');
        const response = await authFetch('/api/auth/me', { skipAuthHandling: true });
        if (!response.ok) {
            log.warn('fetchCurrentUser failed', { status: response.status });
            return null;
        }
        const data = await response.json();
        log.info('Current user fetched', { userId: data.user?._id });
        return data.user || null;
    } catch (error) {
        log.error('fetchCurrentUser error', error);
        return null;
    }
}

function showAuthOverlay() {
    const overlay = document.getElementById('auth-overlay');
    if (overlay) {
        overlay.style.display = 'flex';
    }
}

function hideAuthOverlay() {
    const overlay = document.getElementById('auth-overlay');
    if (overlay) {
        overlay.style.display = 'none';
    }
}

function createAuthOverlay() {
    const overlay = document.createElement('div');
    overlay.id = 'auth-overlay';
    overlay.className = 'rd-login';
    overlay.innerHTML = `
        <aside class="rd-login-aside" aria-hidden="true">
            <div class="rd-login-brand">
                <img src="/images/logo/AppIcon.png" alt="" width="36" height="36" />
                <span class="rd-login-brand-name">Launcher</span>
                <span class="rd-login-brand-pill">Admin</span>
            </div>
            <div class="rd-login-pitch">
                <h2>Console d’administration</h2>
                <p>Comptes, support et suivi technique de Launcher, au même endroit.</p>
            </div>
            <ul class="rd-login-points">
                <li><span class="material-symbols-outlined">group</span>Utilisateurs, rôles et accès</li>
                <li><span class="material-symbols-outlined">support_agent</span>Demandes et tickets support</li>
                <li><span class="material-symbols-outlined">monitoring</span>Erreurs, exécutions et journal d’activité</li>
            </ul>
            <p class="rd-login-foot">Réservé aux comptes administrateur</p>
        </aside>
        <main class="rd-login-main">
            <div class="rd-login-form-wrap">
                <div class="rd-login-mobile-brand">
                    <img src="/images/logo/AppIcon.png" alt="" width="32" height="32" />
                    <span class="rd-login-brand-name">Launcher</span>
                    <span class="rd-login-brand-pill">Admin</span>
                </div>
                <div class="rd-login-heading">
                    <h1>Connexion</h1>
                    <p>Identifiez-vous avec votre compte administrateur.</p>
                </div>
                <form id="login-form" class="rd-login-form">
                    <div class="rd-login-field">
                        <label for="login-email">Adresse e-mail</label>
                        <input type="email" id="login-email" name="email" required autocomplete="email" placeholder="vous@exemple.com">
                    </div>
                    <div class="rd-login-field">
                        <label for="login-password">Mot de passe</label>
                        <input type="password" id="login-password" name="password" required autocomplete="current-password" placeholder="••••••••">
                    </div>
                    <div id="login-feedback" class="rd-login-feedback hidden" role="alert"></div>
                    <button type="submit" id="login-submit-btn">Se connecter</button>
                </form>
            </div>
        </main>
    `;
    
    const form = overlay.querySelector('#login-form');
    const emailInput = overlay.querySelector('#login-email');
    const passwordInput = overlay.querySelector('#login-password');
    const feedback = overlay.querySelector('#login-feedback');
    const submitBtn = overlay.querySelector('#login-submit-btn');

    form.addEventListener('submit', async (e) => {
        e.preventDefault();
        feedback.classList.add('hidden');
        submitBtn.disabled = true;
        submitBtn.textContent = 'Connexion…';

        try {
            const response = await apiFetch('/api/auth/login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email: emailInput.value.trim(),
                    password: passwordInput.value,
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                feedback.textContent = data.error || 'Erreur de connexion';
                feedback.classList.remove('hidden');
                return;
            }

            if (!data.user || data.user.role !== 'admin') {
                feedback.textContent = 'Accès réservé aux administrateurs';
                feedback.classList.remove('hidden');
                return;
            }

            setAuthToken(data.token);
            setCurrentUser(data.user);
            hideAuthOverlay();
            initApp();
        } catch (error) {
            log.error('Login error', error);
            feedback.textContent = 'Erreur de connexion';
            feedback.classList.remove('hidden');
        } finally {
            submitBtn.disabled = false;
            submitBtn.textContent = 'Se connecter';
        }
    });

    return overlay;
}

function createLogoutButton() {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'admin-logout-button';
    btn.title = 'Se déconnecter';
    btn.setAttribute('aria-label', 'Se déconnecter');
    btn.innerHTML = '<span class="material-symbols-outlined" aria-hidden="true">logout</span>';
    btn.addEventListener('click', () => {
        setAuthToken(null);
        setCurrentUser(null);
        location.reload();
    });
    return btn;
}

function createSidebarUser(user) {
    const wrap = document.createElement('div');
    wrap.className = 'ad-sidebar-user';
    const name = (user?.name || user?.username || user?.email || 'Admin').trim();
    const avatar = document.createElement('span');
    avatar.className = 'ad-avatar';
    avatar.setAttribute('aria-hidden', 'true');
    avatar.textContent = name.charAt(0).toUpperCase();
    const text = document.createElement('div');
    text.className = 'ad-sidebar-user-text';
    const nameEl = document.createElement('span');
    nameEl.className = 'ad-sidebar-user-name';
    nameEl.textContent = name;
    const emailEl = document.createElement('span');
    emailEl.className = 'ad-sidebar-user-email';
    emailEl.textContent = user?.email || '';
    text.append(nameEl, emailEl);
    wrap.append(avatar, text, createLogoutButton());
    return wrap;
}

function initApp() {
    const app = document.querySelector('#app');
    app.innerHTML = '';

    // Create admin component
    const adminComponent = createAdminComponent({
        authFetch,
        getCurrentUser: () => currentUser,
    });

    app.appendChild(adminComponent);

    // Utilisateur connecté + déconnexion en pied de menu
    const footer = adminComponent.querySelector('.admin-sidebar-footer') || adminComponent.querySelector('.admin-sidebar-nav');
    if (footer) {
        footer.appendChild(createSidebarUser(currentUser));
    }
}

// Exposer showGlobalNotification et showSuccessModal globalement
window.showGlobalNotification = showGlobalNotification;
window.showSuccessModal = showSuccessModal;

async function bootstrap() {
    log.info('🚀 Admin Dashboard bootstrap starting...');

    try {
        // Create auth overlay
        const authOverlay = createAuthOverlay();
        document.body.appendChild(authOverlay);

        if (getAuthToken()) {
            log.debug('Auth token found, fetching user...');
            const user = await fetchCurrentUser();
            if (user && user.role === 'admin') {
                setCurrentUser(user);
                hideAuthOverlay();
                initApp();
            } else {
                log.warn('Token invalid or user not admin, clearing...');
                setAuthToken(null);
                showAuthOverlay();
            }
        } else {
            showAuthOverlay();
        }

        log.info('✅ Admin Dashboard bootstrap complete');
    } catch (error) {
        log.error('Bootstrap failed', error);
        showGlobalNotification('error', 'Erreur lors du démarrage de l\'application.');
    }
}

bootstrap();
